import type moment from "moment";
import { stringifyUrl } from "query-string";

import { BTLoginTypes, type EmailLinkType, HelpCenterTypes } from "types/enum";

import { getPortalForLoginType, PortalType } from "utilities/portal/portal";

// Only use this for Webforms routes. For React routes, use routes.ts

/**
 * Named browser window/tab target shared by all BTAdmin auto-login (impersonation) links.
 * An admin can only impersonate one user at a time, so reusing a single named tab keeps
 * subsequent impersonations from piling up stale tabs.
 */
export const adminAutoLoginWindowTarget = "autoLoginBT";

export const routesWebforms = {
    urlForLoginType: (loginType: BTLoginTypes, builder: string, sub: string, owner: string) => {
        const portalType = getPortalForLoginType(loginType);
        switch (portalType) {
            case PortalType.BUILDER:
                return builder;
            case PortalType.SUBS:
                return sub;
            case PortalType.OWNER:
                return owner;
            default:
                return "";
        }
    },
    BTAdmin: {
        adminAutoLogin: "/btadmin/adminAutoLogin.aspx",
        getAdminAutoLoginLink(loginType: BTLoginTypes, loginId: number, redirectPath?: string) {
            return stringifyUrl({
                url: this.adminAutoLogin,
                query: {
                    loginType: loginType.toString(),
                    id: loginId.toString(),
                    redirectPath: redirectPath,
                },
            });
        },
    },
    Email: {
        GetEmailLinkClickedUrl(linkType: EmailLinkType, encrypted?: boolean, key1?: string) {
            return `/EmailLinkClicked.aspx?elt=${linkType}${
                encrypted !== undefined ? `&encr=${encrypted}` : ""
            }${key1 !== undefined ? `&key1=${key1}` : ""}`;
        },
    },
    Dropbox: {
        OpenAuth(url: string) {
            // eslint-disable-next-line no-restricted-syntax
            if ((window as any).btMaster && (window as any).btMaster.btDialogs) {
                // DO NOT COPY. THIS IS AN OLD PATTERN. SEE bt-111516 for new pattern.
                // eslint-disable-next-line no-restricted-syntax
                (window as any).btMaster.btDialogs.fireDialogWithID("divDropboxPopup", {
                    left: 35,
                    width: 600,
                    height: 600,
                    src: url,
                    showLoadingSpinner: true,
                    usesPostBacks: false,
                    unloadOnClose: true,
                });
            }
        },
    },
    Media: {
        GetPhotoDrawUrl(
            id: number,
            builderId: number,
            loginType: BTLoginTypes,
            isOnNewEntity: boolean,
            isOnTempFile: boolean,
        ) {
            const params = `?id=${id}&isOnNewEntity=${isOnNewEntity}&isDrawingOnTempFile=${isOnTempFile}&isReadOnly=false&isFromCfv=false&breakDocumentLinks=false&isModal=true`;
            return routesWebforms.urlForLoginType(
                loginType,
                `/Media/Photos/Builder/BuilderPhotoDraw.aspx${params}`,
                `/Media/Photos/Subs/SubsPhotoDraw.aspx${params}&builderId=${builderId}`,
                "",
            );
        },
    },
    SignatureRequest: {
        GetSignUrl(
            docInstanceId: number,
            loginType: BTLoginTypes,
            fromScheduleModal: boolean = false,
            fromReact: boolean = true,
            builderId?: number,
        ) {
            return routesWebforms.urlForLoginType(
                loginType,
                `/PdfSignature/PdfSignatureSigning.aspx?dmethod=iframe&divId=divBasePopupWithIFrame&fromReact=${fromReact}&fromScheduleModal=${fromScheduleModal}&id=${docInstanceId}&masterPageBuilderId=0&dialogID=divBasePopupWithIFrame`,
                `/PdfSignature/SubPdfSignatureSigning.aspx?dmethod=iframe&divId=divBasePopupWithIFrame&fromReact=${fromReact}&id=${docInstanceId}&dialogID=divBasePopupWithIFrame&builderId=${builderId}`,
                `/PdfSignature/OwnerPdfSignatureSigning.aspx?dmethod=iframe&divId=divBasePopupWithIFrame&fromReact=${fromReact}&id=${docInstanceId}&dialogID=divBasePopupWithIFrame`,
            );
        },
    },
    Help: {
        GetHelpCenterLink(loginType: BTLoginTypes) {
            switch (loginType) {
                case BTLoginTypes.BUILDER:
                    return `/HttpHandlers/HelpUrlHandler.ashx?help_url=${HelpCenterTypes.HelpCenter}`;
                case BTLoginTypes.SUBS:
                    return `/HttpHandlers/HelpUrlHandler.ashx?help_url=${HelpCenterTypes.SubHelpCenter}`;
                case BTLoginTypes.OWNER:
                case BTLoginTypes.CONTACT:
                    return `/HttpHandlers/HelpUrlHandler.ashx?help_url=${HelpCenterTypes.OwnerHelpCenter}`;
                default:
                    return "";
            }
        },
        GetThreeDsHelpLink() {
            return `/HttpHandlers/HelpUrlHandler.ashx?help_url=${HelpCenterTypes.ThreeDs}`;
        },
        GetExternalCalendarIntegrationLink() {
            return `/HttpHandlers/HelpUrlHandler.ashx?help_url=${HelpCenterTypes.CalendarFeedGuide}`;
        },
        GetQuickbooksHelp() {
            return `/HttpHandlers/HelpUrlHandler.ashx?help_url=${HelpCenterTypes.QuickBooksHelp}`;
        },
        GetXeroHelp() {
            return `/HttpHandlers/HelpUrlHandler.ashx?help_url=${HelpCenterTypes.XeroHelp}`;
        },
        GetChaseBankAccountHelp() {
            return `/HttpHandlers/HelpUrlHandler.ashx?help_url=${HelpCenterTypes.ChaseBankAccounts}`;
        },
    },
    closeModal<T extends ISerializableObject>(data?: T): void {
        const eventMessage: IDialogEventMessage = { type: "closeModal", data: data };
        window.parent.postMessage(JSON.stringify(eventMessage), "*");
    },
    closeModalAndReload<T extends ISerializableObject>(data?: T): void {
        const eventMessage: IDialogEventMessage = { type: "closeModalAndReload", data: data };
        window.parent.postMessage(JSON.stringify(eventMessage), "*");
    },
    /**
     * @param width specify the width of the modal, use null to keep the width the same
     * @param height specify the height of the modal, use null to keep the height the same
     */
    setModalSize(width: number | null, height: number | null): void {
        const eventMessage: IDialogEventMessage = { type: "setModalSize", data: { width, height } };
        window.parent.postMessage(JSON.stringify(eventMessage), "*");
    },
    reloadAfterClose(): void {
        const eventMessage: IDialogEventMessage = { type: "reloadAfterClose", data: null };
        window.parent.postMessage(JSON.stringify(eventMessage), "*");
    },
    /**
     * Replaces the current history entry's URL without reloading. Only for legacy Webforms-hosted
     * pages that lack a React Router context; use `history.replace` from React Router elsewhere.
     * A bare "?" resets the URL to the current path with no query string.
     */
    replaceWebformsHistoryState(url: string): void {
        const resolvedUrl =
            url === "?" ? location.protocol + "//" + location.host + location.pathname : url;
        // eslint-disable-next-line no-restricted-syntax
        (window as any).history.replaceState(null, "", resolvedUrl);
    },
};

interface ISerializableObject {
    // don't allow serializing functions
    [key: string]: number | moment.Moment | Date | boolean | string;
}

export interface IDialogEventMessage<CloseCallbackData = any> {
    type: "closeModal" | "closeModalAndReload" | "setModalSize" | "reloadAfterClose";
    data?: CloseCallbackData;
}

// please talk to architecture before adding new classes/functions/interfaces
